<script setup lang="ts">
    import { onMounted, ref } from 'vue';
    import { http } from '@/shared/httpWrapper';
    import { ChartData, getRecentStats, RecentStatsResponse } from '@/shared/helpers/api';
    import { Chart, CategoryScale, LinearScale, PointElement, LineElement, ChartOptions, Colors, Tooltip, Filler, Title } from 'chart.js';
    import { Line as LineChart } from 'vue-chartjs';
    import { getLibraryLink, getClientApplicationDetailsLink } from '@/shared/helpers/routing';
    import UsageStatsBackLink from './UsageStatsBackLink.vue';
    import { isAdministrator } from '@/shared/directives/requirePermission';

    Chart.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Colors,
        Tooltip,
        Filler,
        Title
    );

    const stats = ref<RecentStatsResponse | null>();
    const hasAdminAccess = ref<boolean>(false);
    const loading = ref<boolean>(false);

    const usageByTimeOptions = {
        datasets: {
            line: {
                fill: true,
                pointRadius: 0.5,
                borderWidth: 2
            }
        },
        scales: {
            x: {
                ticks: {
                    callback: function(_value, index, _ticks) {
                        // Drop date from labels
                        return stats.value!.usageByTime.labels[index].substring(11);
                    }
                }
            }
        }
    } as ChartOptions<'line'>;

    async function load(): Promise<void> {
        try {
            loading.value = true;
            stats.value = await getRecentStats(http, 10, 24);
        } finally {
            loading.value = false;
        }
    }

    onMounted(async () => {
        await load();
        hasAdminAccess.value = await isAdministrator();
    });

    function chartData(index: number): ChartData {
        return {
            labels: stats.value!.usageByTime.labels,
            datasets: [stats.value!.usageByTime.datasets[index]]
        };
    }

</script>

<template>
    <b-button
        class="is-pulled-right"
        icon-left="refresh"
        @click="load">
        Refresh
    </b-button>
    <h1 class="title">
        Recent API usage
    </h1>
    <h2 class="subtitle mb-1">
        Usage events from the last <b>24</b> hours (<b>10</b> minute intervals)
    </h2>
    <usage-stats-back-link />

    <b-loading
        v-model="loading"
        :is-full-page="true"
        :can-cancel="false" />

    <hr>

    <div class="columns">
        <div class="column">
            <b>Requests</b>
            <line-chart
                v-if="stats"
                :data="chartData(0)"
                :options="usageByTimeOptions"
                style="max-height: 550px;" />
        </div>
        <div class="column">
            <b>Resources</b>
            <line-chart
                v-if="stats"
                :data="chartData(1)"
                :options="usageByTimeOptions"
                style="max-height: 550px;" />
        </div>
        <div class="column">
            <b>Users</b>
            <line-chart
                v-if="stats"
                :data="chartData(2)"
                :options="usageByTimeOptions"
                style="max-height: 550px;" />
        </div>
    </div>

    <hr>

    <div class="columns">
        <div class="column">
            <b-table
                :data="stats?.resources"
                striped
                narrowed>
                <b-table-column
                    v-slot="tableProps"
                    field=""
                    label=""
                    width="20px"
                    centered>
                    <b-icon
                        :icon="tableProps.row.resourceType === 1 ? 'book-open-variant' : 'help-circle'"
                        size="is-small" />
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="resourceName"
                    label="Resource"
                    sortable>
                    <router-link
                        v-if="tableProps.row.resourceType === 1 && tableProps.row.resourceName[0] !== '_'"
                        :to="getLibraryLink(tableProps.row.resourceName)">
                        {{ tableProps.row.resourceName }}
                    </router-link>
                    <span v-else>
                        {{ tableProps.row.resourceName }}
                    </span>
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="requestCount"
                    label="Requests"
                    numeric
                    sortable>
                    {{ tableProps.row.requestCount }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="userCount"
                    label="Users"
                    numeric
                    sortable>
                    {{ tableProps.row.userCount }}
                </b-table-column>
            </b-table>
        </div>
        <div class="column">
            <b-table
                :data="stats?.users"
                striped
                narrowed>
                <b-table-column
                    v-slot="tableProps"
                    field=""
                    label=""
                    width="20px"
                    centered>
                    <b-icon
                        :icon="tableProps.row.clientName ? 'server' : 'account'"
                        size="is-small" />
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="userName"
                    label="User">
                    <router-link
                        v-if="tableProps.row.clientName && tableProps.row.userId"
                        :to="getClientApplicationDetailsLink(tableProps.row.userId)">
                        {{ tableProps.row.clientName }}
                    </router-link>
                    <span v-else>
                        {{ tableProps.row.userName }}
                    </span>
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="requestCount"
                    label="Requests"
                    numeric
                    sortable>
                    {{ tableProps.row.requestCount }}
                </b-table-column>
                <b-table-column
                    v-slot="tableProps"
                    field="resourceCount"
                    label="Resources"
                    numeric
                    sortable>
                    {{ tableProps.row.resourceCount }}
                </b-table-column>
            </b-table>
            <b-notification
                v-if="hasAdminAccess"
                :closable="false"
                class="mt-2">
                Note: Only administrators will see personal usage broken down into individual user accounts.
                Users with less access will see them grouped as "other users".
            </b-notification>
        </div>
    </div>
</template>
